import React, {useState, useRef, useCallback, useMemo} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Modal,
  Pressable,
  Animated,
  PanResponder,
  ActivityIndicator,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {ChatMessage} from '../../types/diretta';
import {Colors} from '../../theme/colors';
import {useAuth} from '../../hooks/useAuth';
import {useTheme} from '../../hooks/useTheme';
import {Icon} from '../Icon';
import {sendReaction} from '../../services/diretta';
import {getStoredToken} from '../../services/auth';

interface ChatTabProps {
  messages: ChatMessage[];
  matchId: string;
  onSendMessage: (message: string, replyTo?: number) => void;
  loading?: boolean;
  isChatClosed?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
}

// Available reactions
const REACTION_EMOJIS = ['👍', '💜', '⚽', '🔥', '😂', '😢'];

// Avatar colors based on first letter
const AVATAR_COLORS: {[key: string]: string} = {
  A: '#3498db', B: '#2ecc71', C: '#e74c3c', D: '#f39c12',
  E: '#8e44ad', F: '#9b59b6', G: '#16a085', H: '#e67e22',
  I: '#f1c40f', J: '#e84393', K: '#34495e', L: '#3498db',
  M: '#2ecc71', N: '#e74c3c', O: '#f39c12', P: '#8e44ad',
  Q: '#9b59b6', R: '#16a085', S: '#e67e22', T: '#f1c40f',
  U: '#e84393', V: '#34495e', W: '#3498db', X: '#2ecc71',
  Y: '#e74c3c', Z: '#f39c12',
};

function getAvatarColor(letter: string): string {
  const upper = letter.toUpperCase();
  return AVATAR_COLORS[upper] || '#95a5a6';
}

// Parse reply shortcode from message: @@rp:author|snippet|messageId@@body
function parseReplyMeta(raw: string): {
  body: string;
  replyAuthor: string | null;
  replySnippet: string | null;
  replyMessageId: string | null;
} {
  if (!raw || typeof raw !== 'string') {
    return { body: raw || '', replyAuthor: null, replySnippet: null, replyMessageId: null };
  }

  const reWithId = /^(@@rp:|@rp:)([^|]+)\|([^|]*)\|(\d+)@@([\s\S]*)$/;
  const mWithId = raw.match(reWithId);

  if (mWithId) {
    let author: string | null = null;
    let snippet: string | null = null;
    try { if (mWithId[2]) author = decodeURIComponent(mWithId[2]); } catch { author = mWithId[2] || null; }
    try { if (mWithId[3]) snippet = decodeURIComponent(mWithId[3]); } catch { snippet = mWithId[3] || null; }

    return {
      body: (mWithId[5] || '').trimStart(),
      replyAuthor: author,
      replySnippet: snippet,
      replyMessageId: mWithId[4],
    };
  }

  const re = /^(@@rp:|@rp:)([^|]+)\|([^@]*)@@([\s\S]*)$/;
  const m = raw.match(re);

  if (!m) return { body: raw, replyAuthor: null, replySnippet: null, replyMessageId: null };

  let author: string | null = null;
  let snippet: string | null = null;
  try { if (m[2]) author = decodeURIComponent(m[2]); } catch { author = m[2] || null; }
  try { if (m[3]) snippet = decodeURIComponent(m[3]); } catch { snippet = m[3] || null; }

  return { body: (m[4] || '').trimStart(), replyAuthor: author, replySnippet: snippet, replyMessageId: null };
}

// Swipeable wrapper for messages - swipe right to reveal actions
const SWIPE_THRESHOLD = 40;

const SwipeableMessage: React.FC<{
  item: ChatMessage;
  isOwnMessage: boolean;
  onSwipeComplete: () => void;
  children: React.ReactNode;
}> = ({isOwnMessage, onSwipeComplete, children}) => {
  const translateX = useRef(new Animated.Value(0)).current;
  const iconOpacity = translateX.interpolate({
    inputRange: [0, SWIPE_THRESHOLD],
    outputRange: [0, 1],
    extrapolate: 'clamp',
  });

  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, gs) => !isOwnMessage && gs.dx > 12 && Math.abs(gs.dy) < 10,
      onPanResponderMove: (_, gs) => {
        if (gs.dx > 0 && gs.dx <= 80) {
          translateX.setValue(gs.dx);
        }
      },
      onPanResponderRelease: (_, gs) => {
        if (gs.dx >= SWIPE_THRESHOLD) {
          onSwipeComplete();
        }
        Animated.spring(translateX, {
          toValue: 0,
          useNativeDriver: true,
          friction: 8,
        }).start();
      },
      onPanResponderTerminate: () => {
        // Reset when scroll or another gesture steals the responder
        Animated.spring(translateX, {
          toValue: 0,
          useNativeDriver: true,
          friction: 8,
        }).start();
      },
    })
  ).current;

  return (
    <View style={[styles.messageRow, isOwnMessage && styles.ownMessageRow]}>
      {/* Reply icon revealed behind message */}
      {!isOwnMessage && (
        <Animated.View style={[styles.swipeReplyIcon, {opacity: iconOpacity}]}>
          <Icon name="reply" size={16} color={Colors.primary} />
        </Animated.View>
      )}
      <Animated.View
        style={{transform: [{translateX}], flex: 1}}
        {...(!isOwnMessage ? panResponder.panHandlers : {})}
      >
        {children}
      </Animated.View>
    </View>
  );
};

export const ChatTab: React.FC<ChatTabProps> = ({
  messages,
  matchId: _matchId,
  onSendMessage,
  loading,
  isChatClosed = false,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
}) => {
  const {user} = useAuth();
  const {colors, isDark} = useTheme();
  const insets = useSafeAreaInsets();
  const [message, setMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<ChatMessage | null>(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
  const [localReactions, setLocalReactions] = useState<Record<number, Record<string, number[]>>>({});
  const flatListRef = useRef<FlatList>(null);
  const highlightAnim = useRef(new Animated.Value(0)).current;

  // Merge server reactions with local optimistic reactions
  const getReactions = useCallback((item: ChatMessage): Record<string, number[]> => {
    const serverReactions = item.reactions || {};
    const local = localReactions[item.id];
    if (!local) return serverReactions;
    return {...serverReactions, ...local};
  }, [localReactions]);

  // Extract unique users from messages for mentions
  const availableUsers = useMemo(() => {
    const users = new Map<string, string>(); // name -> id (or simpler, just unique names)
    
    // Add current user if exists
    // if (user) users.set(user.name, user.id);

    messages.forEach(msg => {
      const name = msg.user_name || (msg.member ? (msg.member.username || `${msg.member.first_name || ''} ${msg.member.last_name || ''}`.trim()) : 'Tifoso');
      if (name && name !== 'Anonimo' && name !== 'Tifoso' && !users.has(name)) {
        users.set(name, name);
      }
    });
    
    return Array.from(users.keys()).sort();
  }, [messages]);

  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);

  const handleTextChange = (text: string) => {
    setMessage(text);
    
    // Simple detection: check if the word at cursor starts with @
    // Ideally we use selection, but for now let's just check the last word if cursor is at end
    const lastWordMatch = text.match(/@([a-zA-Z0-9\s]*)$/);
    if (lastWordMatch) {
      const query = lastWordMatch[1];
      setMentionQuery(query);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (userName: string) => {
    // If name has spaces, wrap in quotes: @"Name Surname"
    // If simple, just @Name
    const mentionText = userName.includes(' ') ? `@"${userName}" ` : `@${userName} `;
    
    // Replace the query part with the full mention
    const newText = message.replace(/@([a-zA-Z0-9\s]*)$/, mentionText);
    setMessage(newText);
    setShowMentions(false);
  };

  const filteredUsers = useMemo(() => {
    if (!mentionQuery) return availableUsers.slice(0, 5);
    const lower = mentionQuery.toLowerCase();
    return availableUsers
      .filter(u => u.toLowerCase().includes(lower))
      .slice(0, 5);
  }, [availableUsers, mentionQuery]);

  // Use messages in chronological order (oldest first, newest at bottom)
  const displayMessages = useMemo(() => messages, [messages]);

  // Scroll to a message and highlight it
  const scrollToMessage = useCallback((messageId: number) => {
    const index = displayMessages.findIndex(m => m.id === messageId);
    if (index !== -1 && flatListRef.current) {
      flatListRef.current.scrollToIndex({index, animated: true, viewPosition: 0.5});
      setHighlightedMessageId(messageId);
      
      // Blink animation - single longer glow
      highlightAnim.setValue(0);
      Animated.sequence([
        Animated.timing(highlightAnim, {toValue: 1, duration: 600, useNativeDriver: false}),
        Animated.timing(highlightAnim, {toValue: 0, duration: 800, useNativeDriver: false}),
      ]).start(() => {
        setHighlightedMessageId(null);
      });
    }
  }, [displayMessages, highlightAnim]);

  // Handle tap on reply header
  const handleReplyTap = useCallback((messageId: string | null) => {
    if (messageId) {
      scrollToMessage(parseInt(messageId, 10));
    }
  }, [scrollToMessage]);

  const handleSend = () => {
    if (message.trim() && !isChatClosed) {
      let payload = message.trim();
      
      if (replyingTo) {
        const parsed = parseReplyMeta(replyingTo.message || '');
        const baseText = parsed.body.trim();
        const snippet = baseText.length > 12 ? baseText.slice(0, 12) + '...' : baseText;
        let replyAuthor = replyingTo.user_name || 
          replyingTo.member?.username || 
          [replyingTo.member?.first_name, replyingTo.member?.last_name].filter(Boolean).join(' ') || 
          'Redazione LaViola.it';
        if (replyAuthor === 'Anonimo') {
          replyAuthor = 'Redazione LaViola.it';
        }
        payload = `@rp:${encodeURIComponent(replyAuthor)}|${encodeURIComponent(snippet)}|${replyingTo.id}@@${payload}`;
      }
      
      onSendMessage(payload);
      setMessage('');
      setReplyingTo(null);
    }
  };

  const handleReply = (msg: ChatMessage) => {
    setReplyingTo(msg);
    setShowActionMenu(false);
    setSelectedMessage(null);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleLongPress = (msg: ChatMessage) => {
    setSelectedMessage(msg);
    setShowActionMenu(true);
  };

  const handleReaction = useCallback(async (messageId: number, emoji: string) => {
    setShowActionMenu(false);
    setSelectedMessage(null);
    if (!user?.id) return;

    // Find the message to get its server-side reactions
    const msg = messages.find(m => m.id === messageId);
    const serverReactions = msg?.reactions || {};

    // Optimistic update - merge server + local, then toggle
    setLocalReactions(prev => {
      const currentLocal = prev[messageId] || {};
      // Start from server data, override with any existing local state
      const merged: Record<string, number[]> = {...serverReactions, ...currentLocal};
      const existing = merged[emoji] || [];
      if (existing.includes(user.id)) {
        // Toggle off
        merged[emoji] = existing.filter(id => id !== user.id);
        if (merged[emoji].length === 0) delete merged[emoji];
      } else {
        // Toggle on
        merged[emoji] = [...existing, user.id];
      }
      return {...prev, [messageId]: merged};
    });

    // Send to server in background
    const token = await getStoredToken();
    if (token) {
      sendReaction(messageId, emoji, token);
    }
  }, [user, messages]);

  const renderMessageWithMentions = (text: string) => {
    let result = text.replace(/@"([^"]+)"/g, '@$1');
    const mentionRegex = /@(\w+)/g;
    const parts = result.split(mentionRegex);
    
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return (
          <Text key={index} style={[styles.mention, {color: colors.primary}]}>
            @{part}
          </Text>
        );
      }
      return part;
    });
  };

  const getReplyPreviewText = useMemo(() => {
    if (!replyingTo) return '';
    const parsed = parseReplyMeta(replyingTo.message || '');
    return parsed.body;
  }, [replyingTo]);

  const getUserName = (item: ChatMessage): string => {
    if (item.user_name) return item.user_name;
    if (item.member) {
      if (item.member.username) return item.member.username;
      const fullName = [item.member.first_name, item.member.last_name].filter(Boolean).join(' ').trim();
      if (fullName) return fullName;
    }
    return 'Tifoso';
  };

  const getReplyUserName = (): string => {
    if (!replyingTo) return '';
    return getUserName(replyingTo);
  };

  const renderMessage = ({item}: {item: ChatMessage}) => {
    const isOwnMessage = user && user.id === item.user_id;
    const isRedazione = item.is_redazione || item.user_id === 1 || getUserName(item).toLowerCase().includes('redazione');
    const userName = isRedazione ? 'Redazione LaViola.it' : getUserName(item);
    const parsed = parseReplyMeta(item.message || '');
    const hasReply = !!parsed.replyAuthor;
    const avatarColor = isRedazione ? Colors.primary : getAvatarColor(userName.charAt(0));
    const isHighlighted = highlightedMessageId === item.id;

    // Interpolate shadow for highlight
    const highlightShadow = highlightAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 12],
    });
    const highlightShadowOpacity = highlightAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 0.4],
    });

    return (
      <SwipeableMessage
        item={item}
        isOwnMessage={isOwnMessage}
        onSwipeComplete={() => handleReply(item)}
      >
        <Pressable 
          style={[styles.messageRowInner, isOwnMessage && styles.ownMessageRow]}
          onLongPress={() => handleLongPress(item)}
          delayLongPress={300}
        >
          {/* Avatar - only show for others on left */}
          {!isOwnMessage && (
            <View style={[styles.avatar, {backgroundColor: avatarColor}]}>
              <Text style={styles.avatarText}>
                {userName.charAt(0).toUpperCase()}
              </Text>
            </View>
          )}

        <View style={[styles.messageContentWrapper, isOwnMessage && styles.ownMessageContentWrapper]}>
          {/* Username - only for others */}
          {!isOwnMessage && (
            <Text style={[
              styles.userName,
              isRedazione && styles.userNameRedazione,
              {color: colors.primary},
            ]}>
              {userName}
            </Text>
          )}

          {/* Message bubble */}
          <Animated.View style={[
            styles.messageBubble,
            {backgroundColor: isDark ? colors.surface : '#e8e8ee'},
            isOwnMessage && {backgroundColor: isDark ? colors.surfaceVariant : '#d0e2fd'},
            isRedazione && {backgroundColor: isDark ? '#2D1B4E' : '#f0e8ff', borderWidth: 1, borderColor: isDark ? '#441274' : '#d5c8f0'},
            hasReply && {minWidth: 150},
            isHighlighted && {
              shadowColor: isDark ? '#A78BFA' : '#451274',
              shadowOffset: {width: 0, height: 0},
              shadowOpacity: isDark ? 0.9 : highlightShadowOpacity,
              shadowRadius: isDark ? 20 : highlightShadow,
              elevation: isDark ? 20 : highlightShadow,
            },
          ]}>
            {/* Reply header inside bubble - tappable */}
            {hasReply && (
              <TouchableOpacity 
                style={styles.replyHeader}
                onPress={() => handleReplyTap(parsed.replyMessageId)}
                activeOpacity={0.7}
              >
                <View style={[styles.replyHeaderLine, {backgroundColor: colors.primary}]} />
                <View style={[styles.replyHeaderContent, {backgroundColor: isDark ? colors.surfaceVariant : '#f9f5ff'}]}>
                  <Text style={[styles.replyHeaderUser, {color: colors.primary}]}>@{parsed.replyAuthor}</Text>
                  <Text style={[styles.replyHeaderText, {color: colors.textSecondary}]} numberOfLines={1}>
                    {parsed.replySnippet}...
                  </Text>
                </View>
              </TouchableOpacity>
            )}

            <Text style={[styles.messageText, {color: colors.textPrimary}, isOwnMessage && styles.ownMessageText]}>
              {renderMessageWithMentions(parsed.body)}
            </Text>

            {/* Reactions inside bubble */}
            {(() => {
              const reactions = getReactions(item);
              return reactions && Object.keys(reactions).length > 0 ? (
                <View style={styles.reactionsBar}>
                  {Object.entries(reactions).map(([emoji, users]) => {
                    const iReacted = user?.id ? users.includes(user.id) : false;
                    return (
                      <TouchableOpacity
                        key={emoji}
                        style={[styles.reactionPill, iReacted && {backgroundColor: isDark ? 'rgba(69,18,116,0.3)' : 'rgba(69,18,116,0.15)', borderWidth: 1, borderColor: colors.primary}]}
                        onPress={() => handleReaction(item.id, emoji)}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.reactionEmoji}>{emoji}</Text>
                        <Text style={[styles.reactionCount, iReacted && {color: colors.primary, fontWeight: '700'}]}>{users.length}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ) : null;
            })()}
          </Animated.View>
        </View>

        {/* Avatar - only show for own on right */}
        {isOwnMessage && (
          <View style={[styles.avatar, styles.avatarRight, {backgroundColor: avatarColor}]}>
            <Text style={styles.avatarText}>
              {userName.charAt(0).toUpperCase()}
            </Text>
          </View>
        )}
        </Pressable>
      </SwipeableMessage>
    );
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, {backgroundColor: colors.background}]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={-15}
    >
      <FlatList
        ref={flatListRef}
        data={displayMessages}
        renderItem={renderMessage}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={styles.messagesList}
        showsVerticalScrollIndicator={false}
        onEndReached={() => {
          if (hasMore && !loadingMore && onLoadMore) {
            onLoadMore();
          }
        }}
        onEndReachedThreshold={0.3}
        ListFooterComponent={
          loadingMore ? (
            <ActivityIndicator style={{paddingVertical: 16}} color={Colors.primary} />
          ) : messages.length > 0 && !hasMore ? (
            <Text style={styles.endOfChat}>— Fine della cronologia —</Text>
          ) : null
        }
        ListEmptyComponent={
          <View style={styles.emptyChat}>
            <View style={styles.emptyChatIconContainer}>
              <Icon name="comment" size={24} color={colors.primary} />
            </View>
            <Text style={[styles.emptyChatTitle, {color: colors.textPrimary}]}>Chat tifosi</Text>
            <Text style={[styles.emptyChatText, {color: colors.textSecondary}]}>
              Sii il primo a commentare la partita!
            </Text>
          </View>
        }
      />

      {replyingTo && (
        <View style={styles.replyPreview}>
          <View style={styles.replyPreviewLine} />
          <View style={styles.replyPreviewContent}>
            <Text style={styles.replyPreviewLabel}>
              In risposta a {getReplyUserName()}
            </Text>
            <Text style={styles.replyPreviewText} numberOfLines={1}>
              {getReplyPreviewText}
            </Text>
          </View>
          <TouchableOpacity onPress={cancelReply} style={styles.replyPreviewClose}>
            <Icon name="xmark" size={16} color="#888" />
          </TouchableOpacity>
        </View>
      )}



      {user && !isChatClosed ? (
        <View style={[styles.inputContainer, {paddingBottom: insets.bottom + 12, backgroundColor: colors.surface, borderTopColor: colors.border}]}>
          {/* Mention Suggestions Popup (Moved inside input container to stick to it) */}
          {showMentions && filteredUsers.length > 0 && (
            <View style={[styles.mentionsContainer, {backgroundColor: isDark ? colors.surfaceVariant : '#fff', borderColor: colors.border}]}>
              {filteredUsers.map(u => (
                <TouchableOpacity 
                  key={u} 
                  style={[styles.mentionItem, {borderBottomColor: isDark ? 'rgba(255,255,255,0.1)' : '#f0f0f0'}]}
                  onPress={() => handleMentionSelect(u)}
                >
                  <View style={[styles.avatar, {width: 24, height: 24, borderRadius: 12, backgroundColor: getAvatarColor(u.charAt(0))}]}>
                    <Text style={{color: '#fff', fontSize: 10, fontWeight: 'bold'}}>{u.charAt(0).toUpperCase()}</Text>
                  </View>
                  <Text style={[styles.mentionName, {color: colors.textPrimary}]}>{u}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
          <TextInput
            style={[styles.input, {backgroundColor: isDark ? colors.background : '#f5f5f5', color: colors.textPrimary}]}
            value={message}
            onChangeText={handleTextChange}
            placeholder={replyingTo ? `Rispondi a ${getReplyUserName()}...` : 'Invia il tuo messaggio'}
            placeholderTextColor={colors.textSecondary}
            multiline
            maxLength={500}
          />
          <TouchableOpacity
            style={[
              styles.sendButton, 
              {backgroundColor: colors.primary},
              !message.trim() && {backgroundColor: isDark ? colors.surfaceVariant : '#ccc'},
            ]}
            onPress={handleSend}
            disabled={!message.trim()}
          >
            <Icon name="arrow-up" size={18} color="#fff" />
            <Text style={styles.sendButtonText}>Invia</Text>
          </TouchableOpacity>
        </View>
      ) : user && isChatClosed ? (
        <View style={[styles.chatClosedBar, {paddingBottom: insets.bottom + 12}]}>
          <Icon name="lock" size={14} color="#451274" />
          <Text style={styles.chatClosedBarText}>La chat è terminata.</Text>
        </View>
      ) : (
        <View style={[styles.loginPrompt, {paddingBottom: insets.bottom + 12, backgroundColor: colors.surface, borderTopColor: colors.border}]}>
          <Text style={[styles.loginPromptText, {color: colors.textSecondary}]}>
            Accedi per partecipare alla chat
          </Text>
        </View>
      )}

      <Modal
        visible={showActionMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowActionMenu(false)}
      >
        <Pressable 
          style={styles.modalOverlay}
          onPress={() => setShowActionMenu(false)}
        >
          <View style={styles.actionMenu}>
            <TouchableOpacity 
              style={styles.actionMenuItem}
              onPress={() => selectedMessage && handleReply(selectedMessage)}
            >
              <Icon name="reply" size={18} color={Colors.primary} />
              <Text style={styles.actionMenuText}>Rispondi</Text>
            </TouchableOpacity>

            <View style={styles.actionMenuSeparator} />

            <View style={styles.actionMenuReactions}>
              {REACTION_EMOJIS.map((emoji) => (
                <TouchableOpacity
                  key={emoji}
                  style={styles.actionMenuEmoji}
                  onPress={() => selectedMessage && handleReaction(selectedMessage.id, emoji)}
                >
                  <Text style={styles.actionMenuEmojiText}>{emoji}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </Pressable>
      </Modal>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  messagesList: {
    padding: 12,
    paddingBottom: 20,
  },
  messageRow: {
    flexDirection: 'row',
    marginBottom: 12,
    alignItems: 'flex-start',
  },
  swipeReplyIcon: {
    position: 'absolute',
    left: 4,
    top: '50%',
    marginTop: -12,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#f0eaff',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: -1,
  },
  ownMessageRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  messageRowInner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  avatarRight: {
    marginRight: 0,
    marginLeft: 8,
  },
  avatarText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  messageContentWrapper: {
    maxWidth: '75%',
    alignItems: 'flex-start',
  },
  ownMessageContentWrapper: {
    alignItems: 'flex-end',
  },
  messageBubble: {
    backgroundColor: '#e8e8ee',
    borderRadius: 16,
    borderTopLeftRadius: 4,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  ownBubble: {
    backgroundColor: '#d4e7ff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 4,
  },
  redazioneBubble: {
    backgroundColor: '#f7f0ff',
    borderWidth: 1,
    borderColor: '#e1d8ff',
  },
  userName: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.primary,
    marginBottom: 4,
  },
  userNameRedazione: {
    color: Colors.primary,
  },
  messageText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  ownMessageText: {
    color: '#333',
  },
  mention: {
    color: Colors.primary,
    fontWeight: '600',
  },
  replyHeader: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  replyHeaderLine: {
    width: 3,
    backgroundColor: Colors.primary,
    borderRadius: 2,
    marginRight: 8,
  },
  replyHeaderContent: {
    flex: 1,
    backgroundColor: '#f9f5ff',
    borderRadius: 6,
    padding: 6,
    flexShrink: 0,
  },
  replyHeaderUser: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.primary,
    marginBottom: 2,
  },
  replyHeaderText: {
    fontSize: 11,
    color: '#666',
  },
  reactionsBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 6,
    gap: 4,
  },
  reactionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 6,
  },
  reactionEmoji: {
    fontSize: 12,
  },
  reactionCount: {
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 3,
    color: '#666',
  },
  endOfChat: {
    textAlign: 'center',
    color: '#999',
    fontSize: 12,
    marginTop: 20,
    marginBottom: 10,
  },
  replyPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5efff',
    padding: 10,
    marginHorizontal: 12,
    marginBottom: 8,
    borderRadius: 8,
  },
  replyPreviewLine: {
    width: 3,
    height: '100%',
    minHeight: 28,
    backgroundColor: Colors.primary,
    borderRadius: 2,
    marginRight: 10,
  },
  replyPreviewContent: {
    flex: 1,
  },
  replyPreviewLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.primary,
    marginBottom: 2,
  },
  replyPreviewText: {
    fontSize: 12,
    color: '#666',
  },
  replyPreviewClose: {
    padding: 6,
  },
  chatClosedBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0e8ff',
    paddingVertical: 16,
    paddingHorizontal: 20,
    gap: 8,
  },
  chatClosedBarText: {
    fontSize: 14,
    color: '#451274',
    fontWeight: '600',
  },
  chatClosedNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff3cd',
    borderWidth: 1,
    borderColor: '#ffc107',
    borderRadius: 8,
    padding: 10,
    marginHorizontal: 12,
    marginBottom: 8,
    gap: 8,
  },
  chatClosedText: {
    flex: 1,
    fontSize: 13,
    color: '#856404',
    fontWeight: '600',
  },
  inputContainer: {
    flexDirection: 'row',
    padding: 12,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    alignItems: 'flex-end',
  },
  inputContainerDisabled: {
    flexDirection: 'row',
    padding: 12,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    alignItems: 'flex-end',
    opacity: 0.6,
  },
  input: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 10,
    fontSize: 15,
    maxHeight: 100,
    color: '#333',
    borderWidth: 1,
    borderColor: 'rgba(69, 18, 116, 0.15)',
  },
  inputDisabled: {
    backgroundColor: '#f5f5f5',
  },
  sendButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.primary,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 24,
    marginLeft: 10,
    gap: 6,
  },
  sendButtonDisabled: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ccc',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 24,
    marginLeft: 10,
    gap: 6,
  },
  sendButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  loginPrompt: {
    padding: 16,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    alignItems: 'center',
  },
  loginPromptText: {
    color: '#666',
    fontSize: 14,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    color: '#666',
    fontSize: 14,
  },
  emptyChat: {
    alignItems: 'center',
    padding: 40,
  },
  emptyChatIconContainer: {
    marginBottom: 12,
  },
  emptyChatTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  emptyChatText: {
    fontSize: 14,
    color: '#666',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionMenu: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    minWidth: 280,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  actionMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    gap: 12,
  },
  actionMenuText: {
    fontSize: 16,
    color: '#333',
    fontWeight: '500',
  },
  actionMenuSeparator: {
    height: 1,
    backgroundColor: '#eee',
    marginVertical: 8,
  },
  actionMenuReactions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 8,
  },
  actionMenuEmoji: {
    padding: 8,
  },
  actionMenuEmojiText: {
    fontSize: 24,
  },
  mentionsContainer: {
    position: 'absolute',
    bottom: '100%', // Above input container
    marginBottom: 50,
    left: 12,
    right: 12,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    zIndex: 1000,
    overflow: 'hidden',
  },
  mentionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    gap: 12,
  },
  mentionName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
});
